import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../api/client';

const emptyUserForm = {
  username: '',
  displayName: '',
  password: '',
  role: 'player'
};

const ROLE_LABELS = {
  player: '玩家',
  admin: '管理员'
};

const STATUS_LABELS = {
  active: '启用',
  disabled: '停用',
  playing: '游戏中',
  finished: '已结束',
  abandoned: '已中止'
};

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatSigned(value) {
  const number = Number(value || 0);
  return `${number >= 0 ? '+' : ''}${number.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleString();
}

const AdminPage = ({ mode = 'users' }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [gomokuStats, setGomokuStats] = useState([]);
  const [gomokuSessions, setGomokuSessions] = useState([]);
  const [createForm, setCreateForm] = useState(emptyUserForm);
  const [displayNameDrafts, setDisplayNameDrafts] = useState({});
  const [passwordDrafts, setPasswordDrafts] = useState({});
  const [busyAction, setBusyAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function loadUsers() {
    if (mode !== 'users') return;
    const body = await apiRequest('/api/admin/users');
    const nextUsers = Array.isArray(body.users) ? body.users : [];
    setUsers(nextUsers);
    setDisplayNameDrafts(Object.fromEntries(
      nextUsers.map((user) => [user.id, user.displayName || ''])
    ));
  }

  async function loadStats() {
    if (mode === 'texas-holdem') {
      const body = await apiRequest('/api/admin/stats');
      setStats(Array.isArray(body.stats) ? body.stats : []);
    } else if (mode === 'gomoku') {
      const body = await apiRequest('/api/admin/gomoku/stats');
      setGomokuStats(Array.isArray(body.stats) ? body.stats : []);
    }
  }

  async function loadSessions() {
    if (mode === 'texas-holdem') {
      const body = await apiRequest('/api/admin/game-sessions');
      setSessions(Array.isArray(body.sessions) ? body.sessions : []);
    } else if (mode === 'gomoku') {
      const body = await apiRequest('/api/admin/gomoku/sessions');
      setGomokuSessions(Array.isArray(body.sessions) ? body.sessions : []);
    }
  }

  useEffect(() => {
    let active = true;

    async function loadAll() {
      setLoading(true);
      setError('');
      try {
        if (mode === 'users') {
          const usersBody = await apiRequest('/api/admin/users');
          if (!active) {
            return;
          }
          const nextUsers = Array.isArray(usersBody.users) ? usersBody.users : [];
          setUsers(nextUsers);
          setDisplayNameDrafts(Object.fromEntries(
            nextUsers.map((user) => [user.id, user.displayName || ''])
          ));
        } else if (mode === 'texas-holdem') {
          const [statsBody, sessionsBody] = await Promise.all([
            apiRequest('/api/admin/stats'),
            apiRequest('/api/admin/game-sessions')
          ]);
          if (!active) {
            return;
          }
          setStats(Array.isArray(statsBody.stats) ? statsBody.stats : []);
          setSessions(Array.isArray(sessionsBody.sessions) ? sessionsBody.sessions : []);
        } else if (mode === 'gomoku') {
          const [statsBody, sessionsBody] = await Promise.all([
            apiRequest('/api/admin/gomoku/stats'),
            apiRequest('/api/admin/gomoku/sessions')
          ]);
          if (!active) {
            return;
          }
          setGomokuStats(Array.isArray(statsBody.stats) ? statsBody.stats : []);
          setGomokuSessions(Array.isArray(sessionsBody.sessions) ? sessionsBody.sessions : []);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.message || '无法加载管理数据');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAll();
    return () => {
      active = false;
    };
  }, [mode]);

  async function runMutation(actionKey, action, successMessage, refresh) {
    setBusyAction(actionKey);
    setError('');
    setNotice('');
    try {
      await action();
      await refresh();
      setNotice(successMessage);
    } catch (requestError) {
      setError(requestError.message || '管理操作失败');
    } finally {
      setBusyAction('');
    }
  }

  const handleCreateUser = (event) => {
    event.preventDefault();
    runMutation(
      'create-user',
      () => apiRequest('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(createForm)
      }),
      '用户已创建。',
      async () => {
        setCreateForm(emptyUserForm);
        await loadUsers();
      }
    );
  };

  const handleStatusToggle = (user) => {
    const nextStatus = user.status === 'active' ? 'disabled' : 'active';
    runMutation(
      `status-${user.id}`,
      () => apiRequest(`/api/admin/users/${user.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus })
      }),
      '用户状态已更新。',
      loadUsers
    );
  };

  const handleDisplayNameUpdate = (event, user) => {
    event.preventDefault();
    runMutation(
      `display-${user.id}`,
      () => apiRequest(`/api/admin/users/${user.id}/display-name`, {
        method: 'PATCH',
        body: JSON.stringify({ displayName: displayNameDrafts[user.id] || '' })
      }),
      '显示名称已更新。',
      async () => {
        await Promise.all([loadUsers(), loadStats()]);
      }
    );
  };

  const handlePasswordReset = (event, user) => {
    event.preventDefault();
    runMutation(
      `password-${user.id}`,
      () => apiRequest(`/api/admin/users/${user.id}/password`, {
        method: 'PATCH',
        body: JSON.stringify({ password: passwordDrafts[user.id] || '' })
      }),
      '密码已重置。',
      async () => {
        setPasswordDrafts((drafts) => ({ ...drafts, [user.id]: '' }));
        await loadUsers();
      }
    );
  };

  let headerTitle = '管理后台';
  let backPath = '/';
  let backText = '游戏库';

  if (mode === 'texas-holdem') {
    headerTitle = '德州扑克管理';
    backPath = '/games/texas-holdem';
    backText = '德州扑克大厅';
  } else if (mode === 'gomoku') {
    headerTitle = '五子棋管理';
    backPath = '/games/gomoku';
    backText = '五子棋大厅';
  } else if (mode === 'users') {
    headerTitle = '用户管理';
    backPath = '/';
    backText = '游戏库';
  }

  return (
    <main className="page-shell page-shell--themed admin-page">
      <header className="page-hero room-header">
        <div>
          <h1>{headerTitle}</h1>
        </div>
        <Link className="secondary-button secondary-button--stone link-button" to={backPath}>{backText}</Link>
      </header>

      {error && <div className="error-message">{error}</div>}
      {notice && <div className="notice-message">{notice}</div>}

      {mode === 'users' && (
        <>
          <section className="data-section data-card">
            <div className="section-heading">
              <h2>创建用户</h2>
              <span>{loading ? '加载中' : '就绪'}</span>
            </div>
            <form className="admin-create-form" onSubmit={handleCreateUser}>
              <label>
                用户名
                <input
                  minLength="3"
                  required
                  value={createForm.username}
                  onChange={(event) => setCreateForm((form) => ({ ...form, username: event.target.value }))}
                />
              </label>
              <label>
                显示名称
                <input
                  required
                  value={createForm.displayName}
                  onChange={(event) => setCreateForm((form) => ({ ...form, displayName: event.target.value }))}
                />
              </label>
              <label>
                密码
                <input
                  minLength="8"
                  required
                  type="password"
                  value={createForm.password}
                  onChange={(event) => setCreateForm((form) => ({ ...form, password: event.target.value }))}
                />
              </label>
              <label>
                角色
                <select
                  value={createForm.role}
                  onChange={(event) => setCreateForm((form) => ({ ...form, role: event.target.value }))}
                >
                  <option value="player">玩家</option>
                  <option value="admin">管理员</option>
                </select>
              </label>
              <button className="primary-button--dazu" type="submit" disabled={Boolean(busyAction)}>
                {busyAction === 'create-user' ? '创建中...' : '创建用户'}
              </button>
            </form>
          </section>

          <section className="data-section data-card">
            <div className="section-heading">
              <h2>用户</h2>
              <span>{users.length} 个账号</span>
            </div>
            <div className="table-wrap">
              <table className="data-table admin-user-table">
                <thead>
                  <tr>
                    <th>用户</th>
                    <th>显示名称</th>
                    <th>状态</th>
                    <th>访问权限</th>
                    <th>重置密码</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>@{user.username}</strong>
                        <span className="table-subtle">{ROLE_LABELS[user.role] || user.role}</span>
                      </td>
                      <td>
                        <form className="inline-edit" onSubmit={(event) => handleDisplayNameUpdate(event, user)}>
                          <input
                            aria-label={`${user.username} 的显示名称`}
                            value={displayNameDrafts[user.id] || ''}
                            onChange={(event) => setDisplayNameDrafts((drafts) => ({
                              ...drafts,
                              [user.id]: event.target.value
                            }))}
                          />
                          <button className="secondary-button secondary-button--stone" type="submit" disabled={busyAction === `display-${user.id}`}>保存</button>
                        </form>
                      </td>
                      <td>
                        <span className={`status-pill ${user.status === 'active' ? 'status-pill--ready' : ''}`}>
                          {STATUS_LABELS[user.status] || user.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="secondary-button secondary-button--stone"
                          type="button"
                          disabled={busyAction === `status-${user.id}`}
                          onClick={() => handleStatusToggle(user)}
                        >
                          {user.status === 'active' ? '停用' : '启用'}
                        </button>
                      </td>
                      <td>
                        <form className="inline-edit" onSubmit={(event) => handlePasswordReset(event, user)}>
                          <input
                            aria-label={`${user.username} 的新密码`}
                            minLength="8"
                            placeholder="新密码"
                            type="password"
                            value={passwordDrafts[user.id] || ''}
                            onChange={(event) => setPasswordDrafts((drafts) => ({
                              ...drafts,
                              [user.id]: event.target.value
                            }))}
                          />
                          <button className="secondary-button secondary-button--stone" type="submit" disabled={busyAction === `password-${user.id}`}>重置</button>
                        </form>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="5">{loading ? '正在加载用户...' : '未找到用户。'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {mode === 'texas-holdem' && (
        <>
          <section className="data-section data-card">
            <div className="section-heading">
              <h2>玩家统计</h2>
              <button className="secondary-button secondary-button--stone compact-button" type="button" onClick={loadStats}>
                刷新
              </button>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>玩家</th>
                    <th>局数</th>
                    <th>胜场</th>
                    <th>手数</th>
                    <th>净筹码</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((row) => (
                    <tr key={row.userId}>
                      <td>
                        <strong>{row.displayName || row.username}</strong>
                        <span className="table-subtle">@{row.username}</span>
                      </td>
                      <td>{formatNumber(row.gamesPlayed)}</td>
                      <td>{formatNumber(row.gamesWon)}</td>
                      <td>{formatNumber(row.handsPlayed)}</td>
                      <td className={Number(row.netChips || 0) >= 0 ? 'net-positive' : 'net-negative'}>
                        {formatSigned(row.netChips)}
                      </td>
                    </tr>
                  ))}
                  {stats.length === 0 && (
                    <tr>
                      <td colSpan="5">{loading ? '正在加载玩家统计...' : '还没有已完成的玩家统计。'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="data-section data-card">
            <div className="section-heading">
              <h2>最近游戏场次</h2>
              <button className="secondary-button secondary-button--stone compact-button" type="button" onClick={loadSessions}>
                刷新
              </button>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>场次</th>
                    <th>状态</th>
                    <th>起始筹码</th>
                    <th>开始时间</th>
                    <th>结束时间</th>
                    <th>结束者</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td>#{session.id}</td>
                      <td>{STATUS_LABELS[session.status] || session.status}</td>
                      <td>{formatNumber(session.startingChips)}</td>
                      <td>{formatDate(session.startedAt)}</td>
                      <td>{formatDate(session.endedAt)}</td>
                      <td>{session.endedByUserId ? `用户 ${session.endedByUserId}` : '-'}</td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan="6">{loading ? '正在加载对局场次...' : '还没有游戏场次。'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {mode === 'gomoku' && (
        <>
          <section className="data-section data-card">
            <div className="section-heading">
              <h2>五子棋玩家统计</h2>
              <button className="secondary-button secondary-button--stone compact-button" type="button" onClick={loadStats}>
                刷新
              </button>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>玩家</th>
                    <th>局数</th>
                    <th>胜场</th>
                    <th>胜率</th>
                  </tr>
                </thead>
                <tbody>
                  {gomokuStats.map((row) => {
                    const winRate = row.gamesPlayed > 0 
                      ? `${((row.gamesWon / row.gamesPlayed) * 100).toFixed(1)}%` 
                      : '0.0%';
                    return (
                      <tr key={row.userId}>
                        <td>
                          <strong>{row.displayName || row.username}</strong>
                          <span className="table-subtle">@{row.username}</span>
                        </td>
                        <td>{formatNumber(row.gamesPlayed)}</td>
                        <td>{formatNumber(row.gamesWon)}</td>
                        <td>{winRate}</td>
                      </tr>
                    );
                  })}
                  {gomokuStats.length === 0 && (
                    <tr>
                      <td colSpan="4">{loading ? '正在加载统计...' : '还没有已完成的五子棋统计。'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="data-section data-card">
            <div className="section-heading">
              <h2>五子棋最近游戏</h2>
              <button className="secondary-button secondary-button--stone compact-button" type="button" onClick={loadSessions}>
                刷新
              </button>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>场次</th>
                    <th>状态</th>
                    <th>黑方</th>
                    <th>白方</th>
                    <th>获胜方</th>
                    <th>开始时间</th>
                    <th>结束时间</th>
                  </tr>
                </thead>
                <tbody>
                  {gomokuSessions.map((session) => {
                    let winnerDisplay = '-';
                    if (session.status === 'finished') {
                      winnerDisplay = session.winnerDisplayName 
                        ? `${session.winnerDisplayName} (@${session.winnerUsername})` 
                        : '平局';
                    }
                    return (
                      <tr key={session.id}>
                        <td>#{session.id}</td>
                        <td>{STATUS_LABELS[session.status] || session.status}</td>
                        <td>
                          <strong>{session.blackDisplayName || session.blackUsername}</strong>
                          <span className="table-subtle">@{session.blackUsername}</span>
                        </td>
                        <td>
                          <strong>{session.whiteDisplayName || session.whiteUsername}</strong>
                          <span className="table-subtle">@{session.whiteUsername}</span>
                        </td>
                        <td>{winnerDisplay}</td>
                        <td>{formatDate(session.startedAt)}</td>
                        <td>{formatDate(session.endedAt)}</td>
                      </tr>
                    );
                  })}
                  {gomokuSessions.length === 0 && (
                    <tr>
                      <td colSpan="7">{loading ? '正在加载对局...' : '还没有五子棋游戏场次。'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default AdminPage;
